import { useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { showSnackbar } from "@/lib/snackbarUtils";
import { useErrorBanner } from "@/providers/ErrorBannerProvider/useErrorBanner";
import { FINALIZE_STUDENT_ROSTER_MUTATION } from "../queries";
import { FinalizeStudentRosterMutation, FinalizeStudentRosterMutationVariables } from "../types";

interface UseFinalizeEnrollmentsProps {
  universityId: string;
  /** Unfiltered eligible roster size — used for failure copy, not the current search page. */
  rosterTotalCount: number;
  refetchQueries?: Array<{ query: any; variables: any }>;
}

export function useFinalizeEnrollments({
  universityId,
  rosterTotalCount,
  refetchQueries,
  onError
}: UseFinalizeEnrollmentsProps & { onError?: (message: string) => void }) {
  const navigate = useNavigate();
  const { setError } = useErrorBanner();
  const errorHandler = onError || setError;

  const [finalizeStudentRoster, { loading }] = useMutation<
    FinalizeStudentRosterMutation,
    FinalizeStudentRosterMutationVariables
  >(FINALIZE_STUDENT_ROSTER_MUTATION, {
    ...(refetchQueries && { refetchQueries })
  });

  const failureCountLabel = () => `${rosterTotalCount} student${rosterTotalCount === 1 ? "" : "s"} could not be added`;

  const handleBusinessLogicErrors = (errors: Array<{ field: string | null; base: string }>) => {
    if (errors && errors.length > 0) {
      const cleanError = extractValidationError(errors[0].base);
      errorHandler(`${failureCountLabel()}, please check their information:\n${cleanError}`);
    } else {
      errorHandler(`${failureCountLabel()}, please check their information and try again`);
    }
  };

  const handleGraphQLErrors = (errorMessage: string) => {
    const cleanError = extractValidationError(errorMessage);
    errorHandler(`${failureCountLabel()}, please check their information:\n${cleanError}`);
  };

  const extractValidationError = (errorMsg: string): string => {
    if (errorMsg.includes("Validation failed:")) {
      const parts = errorMsg.split("Validation failed: ");
      return parts[1];
    }
    return errorMsg.replace("An unexpected error occurred: Failed to enroll student", "").replace(/\(\): /, "");
  };

  const finalizeEnrollments = async (rosterId: string) => {
    try {
      const { data, errors } = await finalizeStudentRoster({
        variables: {
          rosterId
        }
      });

      if (errors && errors.length > 0) {
        handleGraphQLErrors(errors[0].message);
        return;
      }

      if (data?.finalizeStudentRoster.success) {
        const studentCount = data.finalizeStudentRoster.studentCount;
        const message =
          data.finalizeStudentRoster.message ||
          `${studentCount} student${studentCount === 1 ? "" : "s"} ha${studentCount === 1 ? "s" : "ve"} been finalized`;
        showSnackbar(message);
        navigate(`/education/universities/${universityId}/students`);
      } else {
        handleBusinessLogicErrors(data?.finalizeStudentRoster.errors || []);
      }
    } catch (error: any) {
      handleGraphQLErrors(error.message);
    }
  };

  return {
    finalizeEnrollments,
    loading
  };
}
